import React, { useState } from "react";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import BoardCardTile from "./BoardCardTile";

const BoardColumn = ({
  list,
  index,
  onAddCard,
  onRenameList,
  onArchiveList,
  onCardClick,
  onArchiveCard,
}) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(list.name);
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [cardTitleDraft, setCardTitleDraft] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);

  const cards = list.cards || [];

  const commitRename = () => {
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== list.name) {
      onRenameList(list.uuid, trimmed);
    } else {
      setNameDraft(list.name);
    }
    setIsRenaming(false);
  };

  const cancelRename = () => {
    setNameDraft(list.name);
    setIsRenaming(false);
  };

  const submitCard = () => {
    const trimmed = cardTitleDraft.trim();
    if (!trimmed) return;
    onAddCard(list.uuid, trimmed);
    setCardTitleDraft("");
  };

  return (
    <Draggable draggableId={list.uuid} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`w-72 shrink-0 bg-slate-100 rounded-xl flex flex-col ${
            snapshot.isDragging ? "shadow-lg" : ""
          }`}
          style={{ ...provided.draggableProps.style, maxHeight: "calc(100vh - 140px)" }}
        >
          {/* Header — also the column drag handle */}
          <div
            {...provided.dragHandleProps}
            className="flex items-center justify-between gap-2 px-3 pt-2.5 pb-1.5"
          >
            {isRenaming ? (
              <input
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={commitRename}
                onFocus={(e) => e.target.select()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitRename();
                  } else if (e.key === "Escape") {
                    cancelRename();
                  }
                }}
                maxLength={255}
                className="flex-1 text-sm font-semibold text-slate-800 bg-white border border-violet-400 rounded px-2 py-1 outline-none"
              />
            ) : (
              <h3
                className="flex-1 text-sm font-semibold text-slate-800 px-2 py-1 rounded hover:bg-slate-200 cursor-text truncate"
                onClick={() => {
                  setNameDraft(list.name);
                  setIsRenaming(true);
                }}
                title={list.name}
              >
                {list.name}
              </h3>
            )}
            <span className="text-xs text-slate-400 font-medium">{cards.length}</span>
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ color: "#64748b", padding: "2px" }}
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onArchiveList(list.uuid);
                }}
                sx={{ fontSize: "14px", color: "#dc2626" }}
              >
                Archive list
              </MenuItem>
            </Menu>
          </div>

          {/* Cards */}
          <Droppable droppableId={list.uuid} type="card">
            {(dropProvided, dropSnapshot) => (
              <div
                ref={dropProvided.innerRef}
                {...dropProvided.droppableProps}
                className={`flex-1 overflow-y-auto px-2 min-h-[60px] rounded-lg ${
                  dropSnapshot.isDraggingOver ? "bg-violet-50" : ""
                }`}
              >
                {cards.map((card, idx) => (
                  <BoardCardTile
                    key={card.uuid}
                    card={card}
                    index={idx}
                    onClick={onCardClick}
                    onArchive={(c) => onArchiveCard(c.uuid)}
                  />
                ))}
                {dropProvided.placeholder}
              </div>
            )}
          </Droppable>

          {/* Add card */}
          <div className="p-2">
            {isAddingCard ? (
              <div>
                <textarea
                  autoFocus
                  value={cardTitleDraft}
                  onChange={(e) => setCardTitleDraft(e.target.value)}
                  placeholder="Enter a title for this card…"
                  rows={2}
                  maxLength={255}
                  className="w-full text-sm text-slate-800 bg-white border border-violet-400 rounded-lg px-2.5 py-2 outline-none resize-none"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      submitCard();
                    } else if (e.key === "Escape") {
                      setIsAddingCard(false);
                      setCardTitleDraft("");
                    }
                  }}
                />
                <div className="flex items-center gap-2 mt-1.5">
                  <button
                    className="px-3 py-1.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium"
                    onClick={submitCard}
                  >
                    Add card
                  </button>
                  <button
                    className="text-slate-500 hover:text-slate-700"
                    onClick={() => {
                      setIsAddingCard(false);
                      setCardTitleDraft("");
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="flex items-center gap-1.5 w-full px-2 py-1.5 rounded-lg text-sm text-slate-500 hover:bg-slate-200 hover:text-slate-700"
                onClick={() => setIsAddingCard(true)}
              >
                <AddIcon style={{ fontSize: 18 }} />
                Add a card
              </button>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default BoardColumn;
